import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Spinner, inputCls } from "@/components/common";
import { ShareButton } from "@/components/ShareButton";
import { useMeta } from "@/hooks/useMeta";
import { NotFound } from "@/pages/Content";

export function WaitlistForm({ page, style, label }: { page: string; style: string; label: string }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const join = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/style-waitlist", { page, style, email }); setDone(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) return <p className="text-sm mt-6" data-testid="waitlist-done">Thank you — we'll email you as soon as the first {label} ring arrives.</p>;
  return (
    <form onSubmit={join} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md mx-auto" data-testid="waitlist-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className={inputCls} data-testid="waitlist-email" />
      <button disabled={busy} className="btn-ink px-6 py-3 text-xs tracking-widest uppercase whitespace-nowrap disabled:opacity-50" data-testid="waitlist-submit">Notify me</button>
    </form>
  );
}

export default function StylePage() {
  const { slug = "", style = "" } = useParams();
  const [cfg, setCfg] = useState<any>(null);
  const [data, setData] = useState<any>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/style-tiles/${slug}`).then((r) => setCfg(r.data)).catch(() => setMissing(true)); }, [slug]);
  useEffect(() => { if (cfg) api.get("/products", { params: { collection: slug, [cfg.param]: style, limit: 48 } }).then((r) => setData(r.data)).catch(() => setMissing(true)); }, [cfg, slug, style]);
  const t = cfg?.tiles?.find((x: any) => x.value === style);
  useMeta(t ? `${t.label} Rings` : undefined, t?.intro || t?.explainer);
  if (missing || (cfg && !t)) return <NotFound />;
  if (!t || !data) return <Spinner />;
  const back = `/collections/${slug}`;
  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-12 md:py-16" data-testid="style-page">
      <nav className="text-xs text-neutral-500 mb-8"><Link to={back} className="link-u" data-testid="style-page-back">{slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</Link> / {t.label}</nav>
      <div className="grid md:grid-cols-[1.2fr_1fr] gap-10 items-end mb-14">
        <div><p className="eyebrow text-[10px] text-[var(--c-primary)]">Shop by style</p>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="style-page-title">{t.label} Rings</h1>
          {t.intro && <p className="text-sm md:text-base text-neutral-600 mt-5 max-w-xl whitespace-pre-line" data-testid="style-page-intro">{t.intro}</p>}</div>
        <div className="bg-white border border-neutral-200 p-6" data-testid="style-page-explainer">
          <p className="font-display text-xl">What is {t.label}?</p><p className="text-sm text-neutral-600 mt-2">{t.explainer}</p>
          <div className="mt-4 flex items-center gap-4"><ShareButton path={`${back}/style/${encodeURIComponent(style)}`} title={`${t.label} Rings | Somnio.Co`} label /><Link to={`${back}?${cfg.param}=${encodeURIComponent(style)}`} className="text-xs underline" data-testid="style-page-filter">Filter all rings</Link></div>
        </div>
      </div>
      {data.items.length ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-14" data-testid="style-page-grid">{data.items.map((p: any, i: number) => <ProductCard key={p.id} p={p} index={i} />)}</div>
        : <div className="py-16 text-center" data-testid="style-page-soon"><p className="eyebrow text-[var(--c-primary)]">Coming soon</p><p className="font-display text-2xl mt-3">{t.label} pieces are on their way.</p><WaitlistForm page={slug} style={style} label={t.label} /></div>}
    </div>
  );
}
